import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Pages
import NewHomePage from './pages/NewHomePage';
import { Dashboard } from './pages/Dashboard';
import { WishlistView } from './pages/WishlistView';
import { AccountSettingsPage } from './pages/AccountSettingsPage';
import { WishingWellPage } from './pages/WishingWellPage';

// Components
import { OnboardingFlow } from './components/OnboardingFlow';
import { OnboardingGuard } from './components/OnboardingGuard';

// Content Pages
import { HolidayCalendarPage } from './pages/content/HolidayCalendarPage';
import { SecretSantaPage } from './pages/content/SecretSantaPage';
import { GiftGuidesPage } from './pages/content/GiftGuidesPage';
import { PriceTrackerPage } from './pages/content/PriceTrackerPage';
import { BlogHomePage } from './pages/content/BlogHomePage';
import { BlogPostPage } from './pages/content/BlogPostPage';

// Layout
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';

// Styles
import './index.css';

// Temporary: Environment variable security test
if (process.env.NODE_ENV === 'development') {
  console.log('🔍 Environment Variables Security Test');
  console.log('✅ CLIENT-SIDE (Safe to expose):');
  console.log('REACT_APP_SUPABASE_URL:', process.env.REACT_APP_SUPABASE_URL);
  console.log('REACT_APP_SUPABASE_ANON_KEY:', process.env.REACT_APP_SUPABASE_ANON_KEY);
  console.log('🔒 SERVER-SIDE (Hidden from browser):');
  console.log('SUPABASE_SERVICE_ROLE_KEY:', process.env.SUPABASE_SERVICE_ROLE_KEY);
  console.log('CRON_SECRET:', process.env.CRON_SECRET);
  console.log('ALLOWED_ORIGINS:', process.env.ALLOWED_ORIGINS);
}

// Component to conditionally render header
const ConditionalHeader = () => {
  const location = useLocation();
  const hideHeaderRoutes = ['/'];
  
  if (hideHeaderRoutes.includes(location.pathname)) {
    return null;
  }
  
  return <Header />;
};

// Component to conditionally render footer
const ConditionalFooter = () => {
  const location = useLocation();
  const hideFooterRoutes: string[] = []; // Show footer on all pages including homepage
  
  if (hideFooterRoutes.includes(location.pathname)) {
    return null;
  }
  
  return <Footer />;
};

// Component to get background class based on route
const ConditionalBackground = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const whiteBackgroundRoutes = ['/'];
  
  const backgroundClass = whiteBackgroundRoutes.includes(location.pathname) 
    ? 'min-h-screen bg-white' 
    : 'min-h-screen bg-gray-50';
  
  return <div className={backgroundClass}>{children}</div>;
};

export const App = () => {
  return (
    <Router>
        <ConditionalBackground>
          <ConditionalHeader />
          
          <main>
            <Routes>
              {/* Onboarding route */}
              <Route path="/onboarding" element={<OnboardingFlow />} />
              
              {/* Main app routes - protected by onboarding guard */}
              <Route path="/" element={
                <OnboardingGuard>
                  <NewHomePage />
                </OnboardingGuard>
              } />
              <Route path="/dashboard" element={
                <OnboardingGuard>
                  <Dashboard />
                </OnboardingGuard>
              } />
              <Route path="/wishlist/:id" element={
                <OnboardingGuard>
                  <WishlistView />
                </OnboardingGuard>
              } />
                  <Route path="/account/settings" element={
                    <OnboardingGuard>
                      <AccountSettingsPage />
                    </OnboardingGuard>
                  } />
                  <Route path="/wishing-well" element={<WishingWellPage />} />
                  
                  {/* SEO Content Routes - no onboarding required */}
              <Route path="/holiday-calendar-2025" element={<HolidayCalendarPage />} />
              <Route path="/secret-santa-generator" element={<SecretSantaPage />} />
              <Route path="/gift-guides" element={<GiftGuidesPage />} />
              <Route path="/price-tracker" element={<PriceTrackerPage />} />
              
              {/* Blog routes - no onboarding required */}
              <Route path="/blog" element={<BlogHomePage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
            </Routes>
          </main>
          
          <ConditionalFooter />
          <Toaster position="top-right" />
        </ConditionalBackground>
      </Router>
  );
};

export default App;